import type { ViewMode } from '../types/graph';

interface ControlsProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onExport: () => void;
  clusterMode: boolean;
  onClusterModeChange: (enabled: boolean) => void;
  highlightActive: boolean;
  onClearHighlight: () => void;
}

export function Controls({
  viewMode,
  onViewModeChange,
  onExport,
  clusterMode,
  onClusterModeChange,
  highlightActive,
  onClearHighlight
}: ControlsProps) {
  return (
    <div className="hud-panel" style={{
      padding: 'var(--space-md) var(--space-lg)',
      display: 'flex',
      gap: 'var(--space-md)',
      alignItems: 'center'
    }}>
      {/* View Mode Toggle */}
      <div style={{
        display: 'flex',
        background: 'var(--color-surface)',
        borderRadius: '0.375rem',
        padding: 'var(--space-xs)'
      }}>
        <button
          onClick={() => onViewModeChange('2d')}
          style={{
            padding: 'var(--space-sm) var(--space-xl)',
            background: viewMode === '2d' ? 'var(--color-accent)' : 'transparent',
            color: viewMode === '2d' ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            transition: 'all 0.2s'
          }}
        >
          2D View
        </button>
        <button
          onClick={() => onViewModeChange('3d')}
          style={{
            padding: 'var(--space-sm) var(--space-xl)',
            background: viewMode === '3d' ? 'var(--color-accent)' : 'transparent',
            color: viewMode === '3d' ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            transition: 'all 0.2s'
          }}
        >
          3D View
        </button>
      </div>

      {/* Separator */}
      <div style={{ width: '1px', height: 'var(--space-2xl)', background: 'var(--color-border)' }} />

      {/* Cluster Toggle */}
      <button
        onClick={() => viewMode === '2d' && onClusterModeChange(!clusterMode)}
        style={{
          padding: 'var(--space-sm) var(--space-xl)',
          background: clusterMode ? 'var(--color-cluster)' : 'var(--color-surface)',
          color: viewMode === '3d' ? '#555' : (clusterMode ? 'var(--color-text-primary)' : 'var(--color-text-muted)'),
          border: clusterMode && viewMode !== '3d' ? 'none' : '1px solid var(--color-border)',
          borderRadius: '0.25rem',
          cursor: viewMode === '3d' ? 'not-allowed' : 'pointer',
          fontSize: 'var(--font-size-base)',
          fontWeight: 600,
          opacity: viewMode === '3d' ? 0.5 : 1,
          transition: 'all 0.2s'
        }}
        title={viewMode === '3d' ? 'Clusters available in 2D only' : ''}
      >
        {clusterMode && viewMode === '2d' ? 'Clusters ON' : 'Clusters OFF'}
      </button>

      {/* Clear Highlight */}
      {highlightActive && (
        <button
          onClick={onClearHighlight}
          style={{
            padding: 'var(--space-sm) var(--space-xl)',
            background: '#dc2626',
            color: 'var(--color-text-primary)',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer',
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            transition: 'all 0.2s'
          }}
        >
          Clear Highlight
        </button>
      )}

      {/* Separator */}
      <div style={{ width: '1px', height: 'var(--space-2xl)', background: 'var(--color-border)' }} />

      {/* Export Button */}
      <button
        onClick={onExport}
        style={{
          padding: 'var(--space-sm) var(--space-xl)',
          background: 'var(--color-success)',
          color: 'var(--color-text-primary)',
          border: 'none',
          borderRadius: '0.25rem',
          cursor: 'pointer',
          fontSize: 'var(--font-size-base)',
          fontWeight: 600,
          transition: 'all 0.2s'
        }}
      >
        Export PNG
      </button>

      {/* Help Text */}
      <div className="help-text" style={{
        fontSize: 'var(--font-size-sm)',
        color: '#666',
        marginLeft: 'var(--space-sm)'
      }}>
        {viewMode === '2d' ? (
          <>Drag nodes · Scroll to zoom · Click node to highlight deps</>
        ) : (
          <>Drag to rotate · Scroll to zoom · Click node to highlight deps</>
        )}
      </div>
    </div>
  );
}
